/**
 * Data layer for `native-snowflake-direct-connector`. Thin typed fetch wrappers over /api/v1/native-snowflake-direct-connector.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface NativeSnowflakeDirectConnectorRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListNativeSnowflakeDirectConnectorResponse {
	count: number;
	items: NativeSnowflakeDirectConnectorRow[];
}

export interface CreateNativeSnowflakeDirectConnectorBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/native-snowflake-direct-connector";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListNativeSnowflakeDirectConnectorResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListNativeSnowflakeDirectConnectorResponse>(res);
}

export async function create(body: CreateNativeSnowflakeDirectConnectorBody): Promise<NativeSnowflakeDirectConnectorRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<NativeSnowflakeDirectConnectorRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const nativeSnowflakeDirectConnectorQueryKeys = {
	all: ["native-snowflake-direct-connector"] as const,
	list: () => [...nativeSnowflakeDirectConnectorQueryKeys.all] as const,
	detail: (id: string) => [...nativeSnowflakeDirectConnectorQueryKeys.all, id] as const,
};
